import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { createCrudService } from '@/services/api/createCrudService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'

interface Dept {
  id: string
  companyId: string
  companyBranchId: string | null
  department: string
  status: 'ATIVO' | 'INATIVO'
  branches?: { id: string; systemName: string }[]
}

const DeptsService = createCrudService<Dept>('/my-company/departments', { searchField: 'department' })

export function MyCompanyDepartmentsPage() {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar departamento' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(DeptsService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<Dept | null>(null)

  const exp = useDataTableExport({
    filename: 'departamentos',
    service: DeptsService,
    filters: sf.filters,
    columns: [
      { header: 'Departamento', accessor: (d: Dept) => d.department },
      { header: 'Filiais',      accessor: (d: Dept) => (d.branches ?? []).map(b => b.systemName).join(', ') },
      { header: 'Status',       accessor: (d: Dept) => d.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try {
      await DeptsService.destroy(openDelete.id)
      toast.success({ title: 'Removido', description: 'Departamento removido com sucesso.' })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<Dept>[] = [
    { key: 'department', header: 'Departamento', sortable: true, alwaysVisible: true,
      render: d => <span className="font-medium text-gray-800">{d.department}</span> },
    { key: 'branches', header: 'Filiais',
      render: d => {
        const list = d.branches ?? []
        if (!list.length) return <span className="text-gray-400 italic">Nenhuma</span>
        return (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-medium">{list.length}</span>
            <span className="text-xs text-gray-500 break-words max-w-[16rem]">{list.map(b => b.systemName).join(', ')}</span>
          </span>
        )
      } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: d => (
        <StatusDot value={d.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Departamentos"
        subtitle="Áreas internas da empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Departamentos' }]}
        actions={<Button onClick={() => navigate('/my-company/departments/novo')} iconLeft={<Plus size={14} />}>Novo departamento</Button>}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="mycompany.departments"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(d) => [
          { key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => navigate(`/my-company/departments/${d.id}/editar`) },
          { key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setOpenDelete(d) },
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover departamento?"
        description={`Tem certeza que deseja remover "${openDelete?.department}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
